import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";
import { playSound } from "../../components/helpers/sound";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function UserCollectOnBus() {
  const { t } = useTranslation();

  const [vendors, setVendors] = useState([]);
  const [buses, setBuses] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedBusId, setSelectedBusId] = useState("");
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);

  const [routeMoneyBoxWallets, setRouteMoneyBoxWallets] = useState([]);
  const [forcePayPayers, setForcePayPayers] = useState([]);

  const [selectedPassengerKey, setSelectedPassengerKey] = useState("");
  const [selectedRouteMoneyBoxWalletId, setSelectedRouteMoneyBoxWalletId] = useState("");
  const [selectedPayerWalletId, setSelectedPayerWalletId] = useState("");

  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");

  const getPassengerKey = (row) => String(row.payer?.userno ?? "");

  const resetTransactionSelection = () => {
    setSelectedPassengerKey("");
    setSelectedPayerWalletId("");
    setSelectedRouteMoneyBoxWalletId("");
    setMessage("");
    setMessageVariant("info");
  };

  // =========================================================
  // FETCH VENDORS
  // =========================================================

  const fetchVendors = async () => {
    setLoadingVendors(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/vendors`);
      const vendorList = Array.isArray(res.data) ? res.data : [];

      setVendors(vendorList);
      setSelectedVendorId(vendorList.length === 1 ? String(vendorList[0].vendor_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible vendors", err);
      setVendors([]);
      setSelectedVendorId("");
    } finally {
      setLoadingVendors(false);
    }
  };

  // =========================================================
  // FETCH BUSES
  // =========================================================

  const fetchBuses = async (vendorId) => {
    if (!vendorId) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    setLoadingBuses(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/buses`, {
        params: { vendor_id: vendorId },
      });

      const busList = Array.isArray(res.data) ? res.data : [];

      setBuses(busList);
      setSelectedBusId(busList.length === 1 ? String(busList[0].bus_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible buses", err);
      setBuses([]);
      setSelectedBusId("");
    } finally {
      setLoadingBuses(false);
    }
  };

  // =========================================================
  // EXISTING BACKEND DATA
  // =========================================================

  const fetchRouteMoneyBoxWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/available-wallets`);
      const wallets = Array.isArray(res.data) ? res.data : [];

      setRouteMoneyBoxWallets(wallets.filter((wallet) => wallet?.is_route_money_box));
    } catch (err) {
      console.error("❌", t("user_collect_on_bus.messages.fetch_route_money_box_wallets_failed"), err);
      setRouteMoneyBoxWallets([]);
    }
  };

  const fetchForcePayPayers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/forcepay-payers`);
      setForcePayPayers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌", t("user_collect_on_bus.messages.fetch_force_pay_payers_failed"), err);
      setForcePayPayers([]);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    fetchVendors();
    fetchRouteMoneyBoxWallets();
    fetchForcePayPayers();
  }, []);

  // =========================================================
  // VENDOR / BUS CHANGES
  // =========================================================

  useEffect(() => {
    resetTransactionSelection();

    if (selectedVendorId) {
      fetchBuses(selectedVendorId);
    } else {
      setBuses([]);
      setSelectedBusId("");
    }
  }, [selectedVendorId]);

  useEffect(() => {
    resetTransactionSelection();
  }, [selectedBusId]);

  const handleVendorChange = (e) => {
    setSelectedVendorId(e.target.value);
    setSelectedBusId("");
    resetTransactionSelection();
  };

  const handleBusChange = (e) => {
    setSelectedBusId(e.target.value);
    resetTransactionSelection();
  };

  const vendorDropdownDisabled = loadingVendors || vendors.length <= 1;
  const busDropdownDisabled = loadingBuses || !selectedVendorId || buses.length <= 1;

  // =========================================================
  // BUS-FILTERED PASSENGER DATA
  // =========================================================

  const busForcePayPayers = useMemo(() => {
    if (!selectedBusId) return [];

    return forcePayPayers.filter((row) => String(row.bus_id) === String(selectedBusId));
  }, [forcePayPayers, selectedBusId]);

  const routeMoneyBoxWalletById = useMemo(() => {
    const map = new Map();

    routeMoneyBoxWallets.forEach((wallet) => {
      map.set(String(wallet.wallet_id), wallet);
    });

    return map;
  }, [routeMoneyBoxWallets]);

  // =========================================================
  // PASSENGER OPTIONS
  // =========================================================

  const passengerOptions = useMemo(() => {
    const map = new Map();

    busForcePayPayers.forEach((row) => {
      const passengerKey = getPassengerKey(row);

      if (!passengerKey || map.has(passengerKey)) return;

      map.set(passengerKey, {
        value: passengerKey,
        label: [
          maskString(row.payer?.userno),
          maskFullNames(row.payer?.fullname),
          row.start_point,
          row.end_point,
          maskFullNames(row.payer?.sponsor),
        ].filter(Boolean).join(" | "),
        searchText: [
          row.payer?.userno,
          row.payer?.fullname,
          row.payer?.sponsor,
          row.start_point,
          row.end_point,
          row.route_name,
          row.notes,
        ].filter(Boolean).join(" ").toLowerCase(),
      });
    });

    return Array.from(map.values());
  }, [busForcePayPayers]);

  const filterPassengerOption = (option, inputValue) => {
    if (!inputValue) return true;
    return option.data.searchText.includes(inputValue.toLowerCase());
  };

  const selectedPassengerOption = useMemo(() => {
    return passengerOptions.find((option) => option.value === String(selectedPassengerKey)) || null;
  }, [passengerOptions, selectedPassengerKey]);

  const passengerWalletRows = useMemo(() => {
    if (!selectedPassengerKey) return [];

    return busForcePayPayers.filter((row) => getPassengerKey(row) === String(selectedPassengerKey));
  }, [busForcePayPayers, selectedPassengerKey]);

  const canSubmit = Boolean(
    selectedVendorId &&
    selectedBusId &&
    selectedRouteMoneyBoxWalletId &&
    selectedPayerWalletId
  );

  // =========================================================
  // MESSAGE HELPERS
  // =========================================================

  const extractValueFromMessage = (backendMessage, key) => {
    if (!backendMessage) return "";

    const match = backendMessage.match(new RegExp(`${key}=([^\\s|]+)`));
    return match?.[1] ?? "";
  };

  const getRouteLabel = (selectedRow, routeMoneyBoxWallet) => {
    return selectedRow?.route_name || routeMoneyBoxWallet?.route_name || "-";
  };

  const buildSuccessMessage = (resultStatus, selectedRow, routeMoneyBoxWallet, data) => {
    const backendMessage = data?.message || "";
    const payerFullname = selectedRow?.payer?.fullname || "";
    const route = getRouteLabel(selectedRow, routeMoneyBoxWallet);

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    return [
      `${t("user_collect_on_bus.messages.approved.result.label")}: ${t(`user_collect_on_bus.messages.approved.result.${resultStatus}`) || resultStatus}`,
      `${t("user_collect_on_bus.messages.approved.fullname.label")}: ${payerFullname}`,
      `${t("user_collect_on_bus.messages.approved.route.label")}: ${route}`,
      `${t("user_collect_on_bus.messages.approved.ticket_balance_after.label")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  const buildCancelledMessage = (selectedRow, routeMoneyBoxWallet, data) => {
    const backendMessage = data?.message || "";
    const payerFullname = selectedRow?.payer?.fullname || "";
    const route = getRouteLabel(selectedRow, routeMoneyBoxWallet);

    const resultStatus =
      data?.verdict ||
      extractValueFromMessage(backendMessage, "result_status") ||
      "cancelled";

    const ticketBalanceBefore =
      data?.from_ticket_balance_before ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_before") ??
      extractValueFromMessage(backendMessage, "ticket_balance_before") ??
      selectedRow?.ticket_balance ??
      "";

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    const reasonKey = backendMessage.includes("Not enough ticket balance")
      ? "Not enough ticket balance"
      : "cancelled";

    return [
      `${t("user_collect_on_bus.messages.cancelled.result.label")}: ${t(`user_collect_on_bus.messages.cancelled.result.${resultStatus}`) || resultStatus}`,
      `${t("user_collect_on_bus.messages.cancelled.reason.label")}: ${t(`user_collect_on_bus.messages.cancelled.reason.${reasonKey}`) || reasonKey}`,
      `${t("user_collect_on_bus.messages.cancelled.fullname.label")}: ${payerFullname}`,
      `${t("user_collect_on_bus.messages.cancelled.route.label")}: ${route}`,
      `${t("user_collect_on_bus.messages.cancelled.ticket_balance_before.label")}: ${ticketBalanceBefore}`,
      `${t("user_collect_on_bus.messages.cancelled.ticket_balance_after.label")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  // =========================================================
  // PASSENGER / WALLET SELECTION
  // =========================================================

  const handlePassengerChange = (option) => {
    const passengerKey = option ? option.value : "";

    setSelectedPassengerKey(passengerKey);
    setSelectedPayerWalletId("");
    setSelectedRouteMoneyBoxWalletId("");
    setMessage("");
    setMessageVariant("info");

    if (!passengerKey) return;

    const wallets = busForcePayPayers.filter(
      (row) => getPassengerKey(row) === String(passengerKey)
    );

    if (wallets.length === 1) {
      setSelectedPayerWalletId(String(wallets[0].payer_wallet_id));
      setSelectedRouteMoneyBoxWalletId(String(wallets[0].vendor_wallet_id));
    }
  };

  const handleWalletChange = (e) => {
    const payerWalletId = e.target.value;

    setSelectedPayerWalletId(payerWalletId);
    setSelectedRouteMoneyBoxWalletId("");
    setMessage("");
    setMessageVariant("info");

    if (!payerWalletId) return;

    const selectedRow = passengerWalletRows.find(
      (row) => String(row.payer_wallet_id) === String(payerWalletId)
    );

    if (!selectedRow) return;

    setSelectedRouteMoneyBoxWalletId(String(selectedRow.vendor_wallet_id));
  };

  // =========================================================
  // COLLECT
  // =========================================================

  const handleForcePay = async () => {
    if (!canSubmit) {
      setMessage(
        `${t("user_collect_on_bus.messages.missing_wallets.title")} | ` +
        `${t("user_collect_on_bus.messages.missing_wallets.label")}: ` +
        `${t("user_collect_on_bus.messages.missing_wallets.text")}`
      );

      setMessageVariant("error");
      playSound("error");
      return;
    }

    const selectedRow = busForcePayPayers.find(
      (row) =>
        String(row.payer_wallet_id) === String(selectedPayerWalletId) &&
        String(row.vendor_wallet_id) === String(selectedRouteMoneyBoxWalletId)
    );

    const selectedRouteMoneyBoxWallet = routeMoneyBoxWalletById.get(
      String(selectedRouteMoneyBoxWalletId)
    );

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/driver/collect-on-bus`,
        {
          from_wallet_id: Number(selectedPayerWalletId),
          delta_tickets_from_wallet: -1,
          delta_money_from_wallet: 0,
          to_wallet_id: Number(selectedRouteMoneyBoxWalletId),
          delta_tickets_to_wallet: 1,
          delta_money_to_wallet: 0,
          header: "collect-on-bus",
        }
      );

      const verdict = String(data?.verdict || "").toLowerCase();
      const isSuccess = verdict === "approved" || verdict === "success";

      if (isSuccess) {
        setMessage(buildSuccessMessage(verdict, selectedRow, selectedRouteMoneyBoxWallet, data));
        await fetchRouteMoneyBoxWallets();
        await fetchForcePayPayers();
      } else {
        setMessage(buildCancelledMessage(selectedRow, selectedRouteMoneyBoxWallet, data));
      }

      setMessageVariant(isSuccess ? "success" : "error");
      playSound(isSuccess ? "success" : "error");
    } catch (err) {
      const errData = err?.response?.data || {};
      const errMsg = errData?.message || errData?.detail || t("user_collect_on_bus.messages.error.text");

      setMessage(
        buildCancelledMessage(selectedRow, selectedRouteMoneyBoxWallet, {
          verdict: "cancelled",
          message: errMsg,
        })
      );

      setMessageVariant("error");
      playSound("error");
    } finally {
      setSelectedPassengerKey("");
      setSelectedRouteMoneyBoxWalletId("");
      setSelectedPayerWalletId("");
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <StandardScreen
      topNav={<TopNav title={t("user_collect_on_bus.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            <Card title={t("user_collect_on_bus.title")}>
              <Stack gap="3">

                <FormField label={t("user_collect_on_bus.fields.vendor.label")}>
                  <SelectInput
                    value={selectedVendorId}
                    disabled={vendorDropdownDisabled}
                    onChange={handleVendorChange}
                  >
                    <option value="">
                      {loadingVendors
                        ? t("user_collect_on_bus.fields.vendor.loading")
                        : t("user_collect_on_bus.fields.vendor.placeholder")}
                    </option>

                    {vendors.map((vendor) => (
                      <option key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.vendor_id} | {vendor.vendor_name}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_collect_on_bus.fields.bus.label")}>
                  <SelectInput
                    value={selectedBusId}
                    disabled={busDropdownDisabled}
                    onChange={handleBusChange}
                  >
                    <option value="">
                      {loadingBuses
                        ? t("user_collect_on_bus.fields.bus.loading")
                        : t("user_collect_on_bus.fields.bus.placeholder")}
                    </option>

                    {buses.map((bus) => (
                      <option key={bus.bus_id} value={bus.bus_id}>
                        {bus.bus_id} | {bus.plate_number}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_collect_on_bus.fields.payer.label")}>
                  <Select
                    classNamePrefix="react-select"
                    options={passengerOptions}
                    value={selectedPassengerOption}
                    onChange={handlePassengerChange}
                    placeholder={t("user_collect_on_bus.fields.payer.placeholder")}
                    isClearable
                    isDisabled={!selectedBusId}
                    filterOption={filterPassengerOption}
                  />
                </FormField>

                <FormField label={t("user_collect_on_bus.fields.wallet.label")}>
                  <SelectInput
                    value={selectedPayerWalletId}
                    disabled={!selectedPassengerKey || passengerWalletRows.length <= 1}
                    onChange={handleWalletChange}
                  >
                    <option value="">
                      {t("user_collect_on_bus.fields.wallet.placeholder")}
                    </option>

                    {passengerWalletRows.map((row) => (
                      <option key={row.payer_wallet_id} value={row.payer_wallet_id}>
                        {[
                          row.route_name,
                          row.start_point && row.end_point
                            ? `${row.start_point} → ${row.end_point}`
                            : null,
                          row.walletname,
                        ].filter(Boolean).join(" | ")}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <Button onClick={handleForcePay} disabled={!canSubmit} fullWidth>
                  {t("user_collect_on_bus.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}